import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Box, Layers, RotateCcw, ZoomIn, ZoomOut, Info } from 'lucide-react';

/**
 * ThreeDVisualization component
 * Shows a 3D visualization of the home with mesh unit placements
 * Uses Three.js for rendering
 * 
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data
 * @param {Object} props.meshSystem - Mesh system data
 * @param {string} props.selectedCoverage - Selected coverage level
 * @returns {JSX.Element} ThreeDVisualization component
 */
const ThreeDVisualization = ({
  formData,
  placements, // Receive placements directly
  floorPlanLayout, // <<< Add floorPlanLayout prop
  routerStandard, // Receive routerStandard directly
  // selectedCoverage = 'medium' // Keep if needed for signal spheres, or remove if logic changes
}) => {
  const canvasRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentFloor, setCurrentFloor] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  
  // Check if we're on mobile
  const isMobile = windowWidth < 768;
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Removed getCurrentPlacements function as placements are passed directly
  
  // Initialize Three.js scene
  useEffect(() => {
    // Import Three.js dynamically to avoid SSR issues
    const loadThreeJS = async () => {
      try {
        // We're using dynamic imports to load Three.js only on the client side
        const THREE = await import('three');
        const { OrbitControls } = await import('three/examples/jsm/controls/OrbitControls');
        
        // Get canvas element
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        // Create scene
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xf3f4f6);
        
        // Create camera
        const camera = new THREE.PerspectiveCamera(
          75, 
          canvas.clientWidth / canvas.clientHeight, 
          0.1, 
          1000
        );
        camera.position.set(5, 5, 5);
        
        // Create renderer
        const renderer = new THREE.WebGLRenderer({ 
          canvas, 
          antialias: true 
        });
        renderer.setSize(canvas.clientWidth, canvas.clientHeight);
        renderer.setPixelRatio(window.devicePixelRatio);
        
        // Create controls
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        
        // Create ambient light
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
        scene.add(ambientLight);
        
        // Create directional light
        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
        directionalLight.position.set(5, 10, 5);
        scene.add(directionalLight);

        // --- Constants for Scene Mapping ---
        const SCENE_TARGET_WIDTH = 10; // Desired visual width in Three.js units
        const SCENE_TARGET_DEPTH = 10; // Desired visual depth in Three.js units
        const WALL_HEIGHT = 0.5; // Visual height of walls in Three.js units
        const WALL_THICKNESS = 0.05; // Visual thickness of walls

        // --- Calculate Scaling and Offset ---
        let scaleFactor = 1;
        let offsetX = 0;
        let offsetZ = 0;
        const layoutWidthMeters = floorPlanLayout?.layoutWidthMeters ?? 10; // Default if no layout
        const layoutHeightMeters = floorPlanLayout?.layoutHeightMeters ?? 10; // Default if no layout

        if (layoutWidthMeters > 0 && layoutHeightMeters > 0) {
            const scaleX = SCENE_TARGET_WIDTH / layoutWidthMeters;
            const scaleZ = SCENE_TARGET_DEPTH / layoutHeightMeters;
            scaleFactor = Math.min(scaleX, scaleZ); // Fit within target area

            // Center the layout in the scene
            const scaledWidth = layoutWidthMeters * scaleFactor;
            const scaledDepth = layoutHeightMeters * scaleFactor;
            offsetX = -scaledWidth / 2; // Center along X
            offsetZ = -scaledDepth / 2; // Center along Z
        }
        // --- End Scaling Calculation ---


        // Create floor plane based on scaled layout size
        const floorGeometry = new THREE.PlaneGeometry(layoutWidthMeters * scaleFactor, layoutHeightMeters * scaleFactor);
        const floorMaterial = new THREE.MeshStandardMaterial({ color: 0xdddddd, side: THREE.DoubleSide });
        const floorPlane = new THREE.Mesh(floorGeometry, floorMaterial);
        floorPlane.rotation.x = -Math.PI / 2; // Rotate to be horizontal
        floorPlane.position.set(offsetX + (layoutWidthMeters * scaleFactor / 2), 0, offsetZ + (layoutHeightMeters * scaleFactor / 2)); // Position centered
        scene.add(floorPlane);


        // Create rooms based on floorPlanLayout
        if (floorPlanLayout && floorPlanLayout.rooms) {
            floorPlanLayout.rooms.forEach(room => {
                const roomSceneWidth = room.width * scaleFactor;
                const roomSceneDepth = room.height * scaleFactor;
                // Calculate scene center position for the room
                const roomCenterX = room.x * scaleFactor + (roomSceneWidth / 2) + offsetX;
                const roomCenterZ = room.y * scaleFactor + (roomSceneDepth / 2) + offsetZ;
                const roomY = WALL_HEIGHT / 2; // Position walls slightly above the floor plane

                const wallMaterial = new THREE.MeshStandardMaterial({
                    color: getRoomColor(room.type || 'default'), // Use helper, provide default
                    transparent: true,
                    opacity: 0.6
                });

                // Create walls using scene dimensions
                // North wall
                const northWall = new THREE.Mesh(new THREE.BoxGeometry(roomSceneWidth, WALL_HEIGHT, WALL_THICKNESS), wallMaterial);
                northWall.position.set(roomCenterX, roomY, roomCenterZ - roomSceneDepth / 2);
                scene.add(northWall);

                // South wall
                const southWall = new THREE.Mesh(new THREE.BoxGeometry(roomSceneWidth, WALL_HEIGHT, WALL_THICKNESS), wallMaterial);
                southWall.position.set(roomCenterX, roomY, roomCenterZ + roomSceneDepth / 2);
                scene.add(southWall);

                // East wall
                const eastWall = new THREE.Mesh(new THREE.BoxGeometry(WALL_THICKNESS, WALL_HEIGHT, roomSceneDepth), wallMaterial);
                eastWall.position.set(roomCenterX + roomSceneWidth / 2, roomY, roomCenterZ);
                scene.add(eastWall);

                // West wall
                const westWall = new THREE.Mesh(new THREE.BoxGeometry(WALL_THICKNESS, WALL_HEIGHT, roomSceneDepth), wallMaterial);
                westWall.position.set(roomCenterX - roomSceneWidth / 2, roomY, roomCenterZ);
                scene.add(westWall);

                // Add room label at the center
                addRoomLabel(scene, THREE, roomCenterX, roomY + WALL_HEIGHT, roomCenterZ, room.name); // Position label above walls
            });
        }

        // Add mesh units using the calculated scaleFactor and offsets
        addMeshUnits(scene, THREE, placements, routerStandard, scaleFactor, offsetX, offsetZ);

        // Add signal spheres using the calculated scaleFactor and offsets
        addSignalSpheres(scene, THREE, placements, routerStandard, 'medium', scaleFactor, offsetX, offsetZ); // Using 'medium' for now

        // Adjust camera position based on scaled layout size
        const maxDim = Math.max(layoutWidthMeters * scaleFactor, layoutHeightMeters * scaleFactor);
        camera.position.set(maxDim * 0.7, maxDim * 0.8, maxDim * 0.7); // Adjust camera distance based on size
        controls.target.set(offsetX + (layoutWidthMeters * scaleFactor / 2), 0, offsetZ + (layoutHeightMeters * scaleFactor / 2)); // Target the center of the layout

        // Animation loop
        const animate = () => {
          requestAnimationFrame(animate);
          controls.update();
          renderer.render(scene, camera);
        };
        
        // Handle window resize
        const handleResize = () => {
          camera.aspect = canvas.clientWidth / canvas.clientHeight;
          camera.updateProjectionMatrix();
          renderer.setSize(canvas.clientWidth, canvas.clientHeight);
        };
        
        window.addEventListener('resize', handleResize);
        
        // Start animation
        animate();
        
        // Set loading state
        setIsLoading(false);
        
        // Cleanup
        return () => {
          window.removeEventListener('resize', handleResize);
          scene.clear();
          renderer.dispose();
        };
      } catch (error) {
        console.error('Error loading Three.js:', error);
        setIsLoading(false);
      }
    };
    
    // Update dependencies for the effect
    loadThreeJS();
  }, [formData, placements, routerStandard, floorPlanLayout]); // Add floorPlanLayout dependency

  // --- Removed createFloorPlans and createRoom functions ---
  
  /**
   * Add room label
   * @param {Object} scene - Three.js scene
   * @param {Object} THREE - Three.js library
   * @param {number} x - X position
   * @param {number} y - Y position
   * @param {number} z - Z position (scene coordinates)
   * @param {string} labelText - The text to display
   */
  const addRoomLabel = (scene, THREE, x, y, z, labelText) => {
    // Create canvas for text
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 256;
    canvas.height = 256;
    
    // Draw text
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.font = 'Bold 24px Arial';
    context.fillStyle = '#000000';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(labelText, canvas.width / 2, canvas.height / 2); // Use labelText directly

    // Create texture
    const texture = new THREE.CanvasTexture(canvas);
    
    // Create sprite material
    const material = new THREE.SpriteMaterial({ 
      map: texture,
      transparent: true
    });
    
    // Create sprite
    const sprite = new THREE.Sprite(material);
    sprite.position.set(x, y, z); // Use provided scene coordinates
    sprite.scale.set(1.5, 0.75, 1); // Adjust scale as needed

    // Add sprite to scene
    scene.add(sprite);
  };
  
  /**
   * Format room name
   * @param {string} roomType - Room type
   * @returns {string} Formatted room name
   */
  const formatRoomName = (roomType) => {
    // Convert camelCase to Title Case with spaces
    return roomType
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, str => str.toUpperCase()); // Capitalize first letter
  };
  
  /**
   * Get room color based on room type
   * @param {string} roomType - Room type
   * @returns {number} Room color
   */
  const getRoomColor = (roomType) => {
    switch (roomType) {
      case 'livingRoom':
        return 0x90caf9; // Light blue
      case 'bedroom':
        return 0xce93d8; // Light purple
      case 'kitchen':
        return 0xa5d6a7; // Light green
      case 'bathroom':
        return 0xffcc80; // Light orange
      case 'office':
        return 0xef9a9a; // Light red
      case 'diningRoom':
        return 0xfff59d; // Light yellow
      default:
        return 0xe0e0e0; // Light gray
    }
  };
  
  /**
   * Add mesh units to scene
   * @param {Object} scene - Three.js scene
   * @param {Object} THREE - Three.js library
   * @param {Array} placements - Mesh unit placements
   * @param {string} standard - Router standard
   * @param {number} scaleFactor - Scaling factor from meters to scene units
   * @param {number} offsetX - Scene X offset
   * @param {number} offsetZ - Scene Z offset
   */
  const addMeshUnits = (scene, THREE, placements, standard, scaleFactor, offsetX, offsetZ) => {
    if (!placements) return;

    placements.forEach((placement, index) => {
      // Map meter coordinates (placement.position.x/y) to scene coordinates (sceneX/Z)
      const sceneX = placement.position.x * scaleFactor + offsetX;
      const sceneZ = placement.position.y * scaleFactor + offsetZ;

      // TODO: Implement proper floor calculation if algorithms provide Z coordinate
      // For now, place all slightly above the floor plane (Y=0)
      const sceneY = 0.05; // Place slightly above floor plane

      // Create mesh unit
      const geometry = new THREE.BoxGeometry(0.3, 0.1, 0.2);
      const material = new THREE.MeshStandardMaterial({ 
        color: placement.type === 'primary' ? 0x2196f3 : 0x9c27b0
      });
      const meshUnit = new THREE.Mesh(geometry, material);
      
      // Position mesh unit using calculated scene coordinates
      meshUnit.position.set(sceneX, sceneY, sceneZ);
      
      // Add mesh unit to scene
      scene.add(meshUnit);
    });
  };
  
  /**
   * Add signal spheres to scene
   * @param {Object} scene - Three.js scene
   * @param {Object} THREE - Three.js library
   * @param {Array} placements - Mesh unit placements
   * @param {string} standard - Router standard
   * @param {string} coverageLevel - Coverage level (currently unused, could adjust opacity/radius)
   * @param {number} scaleFactor - Scaling factor from meters to scene units
   * @param {number} offsetX - Scene X offset
   * @param {number} offsetZ - Scene Z offset
   */
  const addSignalSpheres = (scene, THREE, placements, standard, coverageLevel, scaleFactor, offsetX, offsetZ) => {
    if (!placements) return;

    // Define base radii in METERS (adjust these based on realistic ranges)
    const baseRadiusHighMeters = 4 * (standard === 'WIFI_7' ? 1.2 : standard === 'WIFI_6' || standard === 'WIFI_6E' ? 1.1 : 1.0);
    const baseRadiusMediumMeters = 7 * (standard === 'WIFI_7' ? 1.2 : standard === 'WIFI_6' || standard === 'WIFI_6E' ? 1.1 : 1.0);
    const baseRadiusBasicMeters = 10 * (standard === 'WIFI_7' ? 1.2 : standard === 'WIFI_6' || standard === 'WIFI_6E' ? 1.1 : 1.0);

    placements.forEach((placement, index) => {
      // Map meter coordinates to scene coordinates
      const sceneX = placement.position.x * scaleFactor + offsetX;
      const sceneZ = placement.position.y * scaleFactor + offsetZ;
      const sceneY = 0.05; // Place slightly above floor plane

      // Calculate radii in scene units
      const highRadiusScene = baseRadiusHighMeters * scaleFactor;
      const mediumRadiusScene = baseRadiusMediumMeters * scaleFactor;
      const basicRadiusScene = baseRadiusBasicMeters * scaleFactor;

      // Create signal spheres for different coverage levels
      // High speed coverage (innermost)
      const highSpeedGeometry = new THREE.SphereGeometry(highRadiusScene, 32, 32);
      const highSpeedMaterial = new THREE.MeshBasicMaterial({
        color: 0x2196f3, // Blue
        transparent: true,
        opacity: 0.15, // Slightly more visible
        depthWrite: false // Prevent obscuring nodes inside
      });
      const highSpeedSphere = new THREE.Mesh(highSpeedGeometry, highSpeedMaterial);
      highSpeedSphere.position.set(sceneX, sceneY, sceneZ); // Use scene coordinates
      scene.add(highSpeedSphere);

      // Medium speed coverage (middle)
      const mediumSpeedGeometry = new THREE.SphereGeometry(mediumRadiusScene, 32, 32);
      const mediumSpeedMaterial = new THREE.MeshBasicMaterial({
        color: 0x9c27b0, // Purple
        transparent: true,
        opacity: 0.1, // Slightly more visible
        depthWrite: false
      });
      const mediumSpeedSphere = new THREE.Mesh(mediumSpeedGeometry, mediumSpeedMaterial);
      mediumSpeedSphere.position.set(sceneX, sceneY, sceneZ); // Use scene coordinates
      scene.add(mediumSpeedSphere);

      // Basic speed coverage (outermost) - Renamed from 'low'
      const basicSpeedGeometry = new THREE.SphereGeometry(basicRadiusScene, 32, 32);
      const basicSpeedMaterial = new THREE.MeshBasicMaterial({
        color: 0x4caf50, // Green
        transparent: true,
        opacity: 0.07, // Slightly more visible
        depthWrite: false
      });
      const basicSpeedSphere = new THREE.Mesh(basicSpeedGeometry, basicSpeedMaterial);
      basicSpeedSphere.position.set(sceneX, sceneY, sceneZ); // Use scene coordinates
      scene.add(basicSpeedSphere);
    });
  };
      
  
  // Handle floor change
  const handleFloorChange = (floor) => {
    setCurrentFloor(floor);
  };
  
  // Handle reset view
  const handleResetView = () => {
    // Reset camera position
    if (canvasRef.current) {
      // This would be handled by the Three.js controls
      console.log('Reset view');
    }
  };
  
  // Handle zoom in
  const handleZoomIn = () => {
    // Zoom in
    if (canvasRef.current) {
      // This would be handled by the Three.js controls
      console.log('Zoom in');
    }
  };
  
  // Handle zoom out
  const handleZoomOut = () => {
    // Zoom out
    if (canvasRef.current) {
      // This would be handled by the Three.js controls
      console.log('Zoom out');
    }
  };
  
  // Generate floor buttons
  const floorButtons = [];
  for (let i = 0; i < (formData.floors || 1); i++) {
    floorButtons.push(
      <button
        key={i}
        className={`px-3 py-1 text-sm font-medium rounded-md ${
          currentFloor === i
            ? 'bg-purple-600 text-white'
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
        }`}
        onClick={() => handleFloorChange(i)}
      >
        Floor {i + 1}
      </button>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center">
            <Box className="h-5 w-5 text-purple-600 mr-2" />
            3D Visualization
          </h3>
          
          {/* Floor selector (only for multi-floor homes) */}
          {formData.floors > 1 && (
            <div className="flex space-x-2">
              <Layers className="h-5 w-5 text-gray-500 mr-1" />
              <div className="flex space-x-1">
                {floorButtons}
              </div>
            </div>
          )}
        </div>
        
        {/* 3D Visualization */}
        <div className="relative w-full h-[500px] bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
          {/* Loading indicator */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600"></div>
            </div>
          )}
          
          {/* Three.js canvas */}
          <canvas
            ref={canvasRef}
            className="w-full h-full"
          />
          
          {/* Controls */}
          {showControls && !isLoading && (
            <div className="absolute bottom-4 left-4 flex space-x-2">
              <motion.button
                className="p-2 bg-white rounded-full shadow-md text-gray-700 hover:bg-gray-100"
                onClick={handleResetView}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <RotateCcw className="h-5 w-5" />
              </motion.button>
              
              <motion.button
                className="p-2 bg-white rounded-full shadow-md text-gray-700 hover:bg-gray-100"
                onClick={handleZoomIn}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <ZoomIn className="h-5 w-5" />
              </motion.button>
              
              <motion.button
                className="p-2 bg-white rounded-full shadow-md text-gray-700 hover:bg-gray-100"
                onClick={handleZoomOut}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <ZoomOut className="h-5 w-5" />
              </motion.button>
            </div>
          )}
          
          {/* Legend */}
          {!isLoading && (
            <div className="absolute top-4 right-4 bg-white rounded-lg shadow-md p-3 border border-gray-200">
              <h4 className="text-xs font-medium text-gray-700 mb-2">Legend</h4>
              <div className="space-y-2">
                <div className="flex items-center">
                  <div className="w-4 h-4 rounded-full bg-blue-500/30 mr-2"></div>
                  <span className="text-xs text-gray-600">High-Speed Coverage</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 rounded-full bg-purple-500/30 mr-2"></div>
                  <span className="text-xs text-gray-600">Medium-Speed Coverage</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 rounded-full bg-green-500/30 mr-2"></div>
                  <span className="text-xs text-gray-600">Basic Coverage</span>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="mt-4 flex items-start">
          <Info className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-gray-600">
              This 3D visualization shows how wireless signals propagate through your multi-floor home.
              Use mouse to rotate, zoom, and pan the view.
            </p>
            <p className="text-sm text-gray-600 mt-2">
              <span className="font-medium">Note:</span> Signal strength decreases as it passes through floors and ceilings.
              For optimal coverage, consider placing mesh units on different floors.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThreeDVisualization;